import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, CheckCircle, XCircle, Clock, Filter } from 'lucide-react';
import { format, startOfWeek, endOfWeek, subDays } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminTimesheets() {
  const { token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: startOfWeek(new Date(), { weekStartsOn: 1 }),
    end: endOfWeek(new Date(), { weekStartsOn: 1 })
  });
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [calendarOpen, setCalendarOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [entriesRes, employeesRes] = await Promise.all([
        axios.get(`${API}/admin/timesheets`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            start_date: format(dateRange.start, 'yyyy-MM-dd'),
            end_date: format(dateRange.end, 'yyyy-MM-dd'),
            user_id: filterEmployee !== 'all' ? filterEmployee : undefined,
            pending_only: filterStatus === 'pending'
          }
        }),
        axios.get(`${API}/admin/employees`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      let filteredEntries = entriesRes.data;
      if (filterStatus === 'approved') {
        filteredEntries = filteredEntries.filter(e => e.approved);
      }
      
      setEntries(filteredEntries);
      setEmployees(employeesRes.data);
    } catch (error) {
      toast.error('Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  }, [token, dateRange, filterEmployee, filterStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectAll = (checked) => {
    if (checked) {
      const pendingIds = entries
        .filter(e => !e.approved && e.status === 'completed')
        .map(e => e.id);
      setSelectedEntries(pendingIds);
    } else {
      setSelectedEntries([]);
    }
  };

  const handleSelectEntry = (entryId, checked) => {
    if (checked) {
      setSelectedEntries([...selectedEntries, entryId]);
    } else {
      setSelectedEntries(selectedEntries.filter(id => id !== entryId));
    }
  };

  const handleApprove = async (approve = true) => {
    if (selectedEntries.length === 0) {
      toast.error('No entries selected');
      return;
    }

    try {
      await axios.post(`${API}/admin/timesheets/approve`,
        { entry_ids: selectedEntries, approved: approve },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${selectedEntries.length} entries ${approve ? 'approved' : 'rejected'}`);
      setSelectedEntries([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to update entries');
    }
  };

  const formatDuration = (hours) => {
    if (!hours && hours !== 0) return '--:--';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const pendingCount = entries.filter(e => !e.approved && e.status === 'completed').length;
  const totalHours = entries.reduce((sum, e) => sum + (e.total_hours || 0), 0);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold">Timesheets</h1>
            <p className="text-muted-foreground">Review and approve employee time entries</p>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4">
              {/* Date Range */}
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" data-testid="timesheet-date-picker">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    {format(dateRange.start, 'MMM d')} - {format(dateRange.end, 'MMM d')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    selected={{ from: dateRange.start, to: dateRange.end }}
                    onSelect={(range) => {
                      if (range?.from && range?.to) {
                        setDateRange({ start: range.from, end: range.to });
                        setCalendarOpen(false);
                      }
                    }}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>

              {/* Employee Filter */}
              <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                <SelectTrigger className="w-48" data-testid="employee-filter">
                  <SelectValue placeholder="All Employees" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Status Filter */}
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                </SelectContent>
              </Select>

              {/* Bulk Actions */}
              {selectedEntries.length > 0 && (
                <div className="flex items-center gap-2 ml-auto">
                  <span className="text-sm text-muted-foreground">
                    {selectedEntries.length} selected
                  </span>
                  <Button 
                    size="sm" 
                    onClick={() => handleApprove(true)}
                    className="btn-clock-in"
                    data-testid="bulk-approve-btn"
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Approve
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleApprove(false)}
                    data-testid="bulk-reject-btn"
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Reject
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="total-entries-admin">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Entries</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{entries.length}</div>
            </CardContent>
          </Card>
          <Card data-testid="pending-count-admin">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Pending</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">{pendingCount}</div>
            </CardContent>
          </Card>
          <Card data-testid="total-hours-admin">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Hours</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatDuration(totalHours)}</div>
            </CardContent>
          </Card>
          <Card data-testid="employees-count-admin">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Employees</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {new Set(entries.map(e => e.user_id)).size}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Entries Table */}
        <Card>
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="h-8 w-8 spinner border-primary" />
              </div>
            ) : entries.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No timesheet entries found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={selectedEntries.length === pendingCount && pendingCount > 0}
                          onCheckedChange={handleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      </TableHead>
                      <TableHead>Employee</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Clock In</TableHead>
                      <TableHead>Clock Out</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry) => (
                      <TableRow key={entry.id} data-testid={`timesheet-row-${entry.id}`}>
                        <TableCell>
                          {!entry.approved && entry.status === 'completed' && (
                            <Checkbox
                              checked={selectedEntries.includes(entry.id)}
                              onCheckedChange={(checked) => handleSelectEntry(entry.id, checked)}
                              data-testid={`select-entry-${entry.id}`}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{entry.user_name}</p>
                            <p className="text-xs text-muted-foreground">{entry.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {entry.project_name ? (
                            <div className="flex items-center gap-2">
                              <div 
                                className="h-3 w-3 rounded-full flex-shrink-0" 
                                style={{ backgroundColor: entry.project_color || '#6366f1' }}
                              />
                              <div>
                                <span className="text-sm font-medium">{entry.project_name}</span>
                                {entry.task_description && (
                                  <p className="text-xs text-muted-foreground truncate max-w-24">
                                    {entry.task_description}
                                  </p>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>{format(new Date(entry.date + 'T12:00:00'), 'MMM d, yyyy')}</TableCell>
                        <TableCell>{format(new Date(entry.clock_in), 'h:mm a')}</TableCell>
                        <TableCell>
                          {entry.clock_out 
                            ? format(new Date(entry.clock_out), 'h:mm a')
                            : <Badge variant="outline">Active</Badge>
                          }
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatDuration(entry.total_hours)}
                        </TableCell>
                        <TableCell>
                          {entry.status === 'completed' ? (
                            entry.approved ? (
                              <Badge className="badge-approved">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Approved
                              </Badge>
                            ) : (
                              <Badge className="badge-pending">
                                <Clock className="h-3 w-3 mr-1" />
                                Pending
                              </Badge>
                            )
                          ) : (
                            <Badge variant="outline">{entry.status}</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
